import { findParentNodeClosestToPos } from "@tiptap/core";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";

import { adjacentNodeInDirection, isCollapsed, isSectionCollapsible } from "./utils";

import type { Editor } from "@tiptap/core";
import type { Level } from "@tiptap/extension-heading";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Selection as PMSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

export function isCollapsedSectionSelection(selection: PMSelection): selection is NodeSelection {
  return (
    selection instanceof NodeSelection &&
    isCollapsed(selection.node) &&
    isSectionCollapsible(selection.node)
  );
}

export function collapsedSectionSpannedBy(
  selection: PMSelection,
): { node: PMNode; pos: number } | null {
  const node = selection.$from.doc.nodeAt(selection.from);

  if (
    isCollapsed(node) &&
    isSectionCollapsible(node) &&
    selection.to === selection.from + node.nodeSize
  ) {
    return { node, pos: selection.from };
  }

  return null;
}

export function closestCollapsedSection(
  selection: PMSelection,
): { node: PMNode; pos: number } | null {
  if (isCollapsedSectionSelection(selection)) {
    return {
      node: selection.node,
      pos: selection.from,
    };
  }

  const spanned = collapsedSectionSpannedBy(selection);
  if (spanned) return spanned;

  const found = findParentNodeClosestToPos(
    selection.$from,
    (node) => isCollapsed(node) && isSectionCollapsible(node),
  );

  if (found) {
    return {
      node: found.node,
      pos: found.pos,
    };
  }

  return null;
}

export function collapsedSectionInDirection(
  view: EditorView,
  direction: "up" | "down",
): number | null {
  return adjacentNodeInDirection(
    view,
    direction,
    (node) => isCollapsed(node) && isSectionCollapsible(node),
  );
}

export function expandSection(
  editor: Editor,
  pos: number,
  { focus = true, caretPos }: { focus?: boolean; caretPos?: number } = {},
): boolean {
  const node = editor.state.doc.nodeAt(pos);

  if (!isCollapsed(node) || !isSectionCollapsible(node)) {
    return false;
  }

  return editor
    .chain()
    .setCollapsed(pos, false)
    .command(({ tr, dispatch }) => {
      if (dispatch && caretPos !== undefined) {
        tr.setSelection(TextSelection.create(tr.doc, caretPos));
      }
      return true;
    })
    .command(({ commands }) => {
      if (focus) {
        return commands.focus();
      }

      return true;
    })
    .run();
}

/**
 * Where a node ends the active section:
 * - `"none"`   — not a boundary; the node belongs to the section (stays hidden)
 * - `"before"` — exclusive boundary; the node stays visible (e.g. the next heading)
 * - `"after"`  — inclusive boundary; the node is hidden with the section (the closing rule)
 */
export type SectionBoundary = "none" | "before" | "after";

export function sectionBoundary(sectionNode: PMNode, node: PMNode): SectionBoundary {
  switch (sectionNode.type.name) {
    case "heading": {
      if (node.type.name === "heading") {
        const { level: sectionLevel } = sectionNode.attrs as { level: Level };
        const { level } = node.attrs as { level: Level };

        return sectionLevel >= level ? "before" : "none";
      }

      return "none";
    }

    case "horizontalRule": {
      if (node.type.name === "horizontalRule") {
        return "after";
      }

      if (node.type.name === "heading") {
        return "before";
      }

      return "none";
    }

    default: {
      return "none";
    }
  }
}
